import PropTypes from 'prop-types';
// @mui
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { NEXT_VISIT_CHOICES, choiceDay, dayText, nextVisitProblem } from './next-visit';
import { latestNextVisit } from './visit-status';
import { dateOfDay, dayOf } from './todo-text';

// ----------------------------------------------------------------------

// "When should you see this student next?" (#613): the choice asked when a
// visit is logged, and when the date is changed on its own. value is
// { choice, date } ('YYYY-MM-DD' when choice is 'date'); nextVisitPayload turns
// it into what the server takes.

export const EMPTY_NEXT_VISIT = { choice: '', date: null };

const NextVisitField = ({ value, onChange, grade, today, disabled }) => {
  const problem = nextVisitProblem(value, { grade, today });
  const latest = latestNextVisit(grade, today);

  return (
    <FormControl error={!!problem} disabled={disabled}>
      <FormLabel id="next-visit-label">When should you see this student next?</FormLabel>
      <RadioGroup
        aria-labelledby="next-visit-label"
        value={value.choice}
        onChange={(e) => onChange({ ...value, choice: e.target.value })}
      >
        {NEXT_VISIT_CHOICES.map(({ value: choice, label }) => {
          const day = choiceDay(choice, today);
          return (
            <FormControlLabel
              key={choice}
              value={choice}
              control={<Radio size="small" />}
              label={day ? `${label} (${dayText(day)})` : label}
            />
          );
        })}
      </RadioGroup>
      {value.choice === 'date' && (
        <Stack sx={{ mt: 1 }}>
          <DatePicker
            label="Next visit by"
            value={dateOfDay(value.date)}
            onChange={(d) => onChange({ ...value, date: d && !Number.isNaN(d.getTime()) ? dayOf(d) : null })}
            minDate={dateOfDay(today)}
            maxDate={latest ? dateOfDay(latest) : undefined}
            disabled={disabled}
            slotProps={{ textField: { size: 'small', fullWidth: true, error: !!problem } }}
          />
        </Stack>
      )}
      {problem && <FormHelperText>{problem}</FormHelperText>}
      {!problem && latest && (
        <FormHelperText>{`A Grade 12 student's next visit can't be after graduation (${dayText(latest)}).`}</FormHelperText>
      )}
    </FormControl>
  );
};

NextVisitField.propTypes = {
  value: PropTypes.shape({ choice: PropTypes.string, date: PropTypes.string }).isRequired,
  onChange: PropTypes.func.isRequired,
  grade: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  today: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default NextVisitField;
